// Upload utilities for web - matches mobile's storageUploadHooks.ts pattern

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Public URL base for R2 storage
const STORAGE_PUBLIC_URL = "https://storage.tiny.club";

// Image size targets (matching mobile's imageProcessing.ts)
const SMALL_TARGET_SIZE = 128;   // Shortest side = 128px
const MEDIUM_TARGET_SIZE = 1024; // Shortest side = 1024px
const JPEG_QUALITY = 0.85;       // 85% quality

/**
 * Get a pre-signed URL from the Supabase Edge Function.
 */
async function getPreSignedUrl(
  filename: string,
  type: "upload" | "delete"
): Promise<string> {
  const edgeUrl = `${supabaseUrl}/functions/v1/generate-pre-signed-url?type=${type}&key=${filename}`;
  const res = await fetch(edgeUrl, {
    headers: {
      Authorization: `Bearer ${supabaseAnonKey}`,
      "Content-Type": "application/json",
    },
  });

  if (!res.ok) {
    throw new Error(
      `Failed to get ${type} pre-signed URL for ${filename}: ${res.status}`
    );
  }

  const data = await res.json();
  if (!data.url) {
    throw new Error(`Pre-signed URL missing in response for ${filename}`);
  }

  return data.url;
}

/**
 * Upload a file using XMLHttpRequest with progress tracking.
 */
async function uploadFileWithProgress(
  url: string,
  file: File | Blob,
  contentType: string,
  onProgress?: (progress: number) => void,
  timeoutMs: number = 180000 // 3 minutes
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.open("PUT", url, true);
    xhr.setRequestHeader("Content-Type", contentType);
    xhr.timeout = timeoutMs;

    // Track upload progress
    if (xhr.upload && onProgress) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const progress = event.loaded / event.total;
          onProgress(progress);
        }
      };
    }

    // Handle completion
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        if (onProgress) onProgress(1);
        resolve();
      } else {
        reject(
          new Error(`Upload failed with status ${xhr.status}: ${xhr.responseText}`)
        );
      }
    };

    xhr.onerror = () => {
      reject(new Error("Upload failed due to network error."));
    };

    xhr.ontimeout = () => {
      reject(
        new Error(
          `Upload timed out after ${timeoutMs / 1000} seconds. Please check your internet connection.`
        )
      );
    };

    xhr.send(file);
  });
}

export interface ImageUploadResult {
  imageSmall: string;
  imageMedium: string;
  imageFull: string;
  width: number;
  height: number;
  aspectRatio: number;
  // Filenames for cleanup if post is cancelled
  filenames: string[];
}

interface ProcessedImages {
  full: Blob;
  medium: Blob;
  small: Blob;
  width: number;
  height: number;
}

/**
 * Load an image from a File into an HTMLImageElement.
 */
function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load image"));
    };
    img.src = url;
  });
}

/**
 * Resize an image using Canvas API.
 * Matches mobile's logic: resize so shortest side equals targetSize.
 */
function resizeImage(
  img: HTMLImageElement,
  targetShortestSide: number,
  quality: number
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const { naturalWidth: width, naturalHeight: height } = img;
    const shortest = Math.min(width, height);

    // Calculate scale factor based on shortest side
    const scale = targetShortestSide / shortest;
    const newWidth = Math.round(width * scale);
    const newHeight = Math.round(height * scale);

    // Create canvas and draw resized image
    const canvas = document.createElement("canvas");
    canvas.width = newWidth;
    canvas.height = newHeight;

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      reject(new Error("Failed to get canvas context"));
      return;
    }

    // Use high-quality image smoothing
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    ctx.drawImage(img, 0, 0, newWidth, newHeight);

    // Export as JPEG blob
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error("Failed to create image blob"));
        }
      },
      "image/jpeg",
      quality
    );
  });
}

/**
 * Re-encode image as JPEG at specified quality (no resize).
 */
function reencodeImage(
  img: HTMLImageElement,
  quality: number
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const { naturalWidth: width, naturalHeight: height } = img;

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      reject(new Error("Failed to get canvas context"));
      return;
    }

    ctx.drawImage(img, 0, 0);

    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error("Failed to create image blob"));
        }
      },
      "image/jpeg",
      quality
    );
  });
}

/**
 * Process an image into 3 sizes (matching mobile's imageProcessing.ts):
 * - full: JPEG 85% quality (original dimensions)
 * - medium: shortest side = 1024px, JPEG 85%
 * - small: shortest side = 128px, JPEG 85%
 */
async function processImage(file: File): Promise<ProcessedImages> {
  const img = await loadImage(file);
  const { naturalWidth: width, naturalHeight: height } = img;

  // Process all 3 sizes in parallel
  const [full, medium, small] = await Promise.all([
    reencodeImage(img, JPEG_QUALITY),
    resizeImage(img, MEDIUM_TARGET_SIZE, JPEG_QUALITY),
    resizeImage(img, SMALL_TARGET_SIZE, JPEG_QUALITY),
  ]);

  return { full, medium, small, width, height };
}

/**
 * Upload an image to R2 storage.
 * Creates 3 sizes (full, medium, small) matching mobile's behavior.
 */
export async function uploadImage(
  file: File,
  onProgress?: (progress: number) => void
): Promise<ImageUploadResult> {
  // Phase 1: Process image into 3 sizes (0-30% of progress)
  onProgress?.(0);
  const processed = await processImage(file);
  onProgress?.(0.3);

  // Generate unique filenames for all 3 sizes (always JPEG after processing)
  const baseName = Date.now().toString();
  const fullFilename = `${baseName}-full.jpg`;
  const mediumFilename = `${baseName}-medium.jpg`;
  const smallFilename = `${baseName}-small.jpg`;

  // Get pre-signed URLs for all 3 uploads
  const [fullUrl, mediumUrl, smallUrl] = await Promise.all([
    getPreSignedUrl(fullFilename, "upload"),
    getPreSignedUrl(mediumFilename, "upload"),
    getPreSignedUrl(smallFilename, "upload"),
  ]);

  // Phase 2: Upload all 3 sizes (30-100% of progress)
  // Track progress across all 3 uploads
  const progressMap = { full: 0, medium: 0, small: 0 };
  const reportOverallProgress = () => {
    const uploadProgress = (progressMap.full + progressMap.medium + progressMap.small) / 3;
    // Map upload progress (0-1) to overall progress (0.3-1.0)
    onProgress?.(0.3 + uploadProgress * 0.7);
  };

  await Promise.all([
    uploadFileWithProgress(fullUrl, processed.full, "image/jpeg", (p) => {
      progressMap.full = p;
      reportOverallProgress();
    }),
    uploadFileWithProgress(mediumUrl, processed.medium, "image/jpeg", (p) => {
      progressMap.medium = p;
      reportOverallProgress();
    }),
    uploadFileWithProgress(smallUrl, processed.small, "image/jpeg", (p) => {
      progressMap.small = p;
      reportOverallProgress();
    }),
  ]);

  // Return public URLs and filenames for cleanup
  return {
    imageFull: `${STORAGE_PUBLIC_URL}/${fullFilename}`,
    imageMedium: `${STORAGE_PUBLIC_URL}/${mediumFilename}`,
    imageSmall: `${STORAGE_PUBLIC_URL}/${smallFilename}`,
    width: processed.width,
    height: processed.height,
    aspectRatio: processed.width / processed.height,
    filenames: [fullFilename, mediumFilename, smallFilename],
  };
}

export interface VideoUploadResult {
  tempKey: string;
  thumbnailUrl: string;
  width: number;
  height: number;
  aspectRatio: number;
  duration: number;
  // Filenames for cleanup if post is cancelled
  filenames: string[];
}

/**
 * Generate a thumbnail from a video file's first frame using canvas.
 * Resizes so shortest side = 1024px (capped at native resolution) for sharp web display.
 */
function generateVideoThumbnail(file: File): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.preload = "auto";
    video.muted = true;
    const url = URL.createObjectURL(file);

    video.onloadeddata = () => {
      video.currentTime = 0;
    };

    video.onseeked = () => {
      const { videoWidth, videoHeight } = video;
      // Resize so shortest side = 1024px for sharp web display
      const shortest = Math.min(videoWidth, videoHeight);
      const scale = Math.min(MEDIUM_TARGET_SIZE / shortest, 1);
      const thumbWidth = Math.round(videoWidth * scale);
      const thumbHeight = Math.round(videoHeight * scale);

      const canvas = document.createElement("canvas");
      canvas.width = thumbWidth;
      canvas.height = thumbHeight;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        URL.revokeObjectURL(url);
        reject(new Error("Failed to get canvas context for thumbnail"));
        return;
      }
      ctx.drawImage(video, 0, 0, thumbWidth, thumbHeight);
      canvas.toBlob(
        (blob) => {
          URL.revokeObjectURL(url);
          if (blob) resolve(blob);
          else reject(new Error("Failed to create thumbnail blob"));
        },
        "image/jpeg",
        JPEG_QUALITY
      );
    };

    video.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load video for thumbnail generation"));
    };

    video.src = url;
  });
}

/**
 * Extract video metadata (dimensions and duration) using a hidden video element.
 */
function getVideoMetadata(file: File): Promise<{ width: number; height: number; duration: number }> {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.preload = "metadata";
    const url = URL.createObjectURL(file);

    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve({
        width: video.videoWidth,
        height: video.videoHeight,
        duration: video.duration,
      });
    };
    video.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load video metadata"));
    };
    video.src = url;
  });
}

/**
 * Upload a video to R2 storage as a temp file for Coconut processing.
 * Also generates and uploads a thumbnail from the first frame.
 * Matches mobile's flow: upload raw video + thumbnail, then caller invokes process-video-upload edge function.
 */
export async function uploadVideo(
  file: File,
  onProgress?: (progress: number) => void
): Promise<VideoUploadResult> {
  // Phase 1: Extract metadata + generate thumbnail (0-15% of progress)
  onProgress?.(0);
  const [metadata, thumbnailBlob] = await Promise.all([
    getVideoMetadata(file),
    generateVideoThumbnail(file),
  ]);
  onProgress?.(0.1);

  // Generate unique filenames - video goes to temp location
  const baseName = Date.now().toString();
  const ext = file.name.split(".").pop() || "mp4";
  const tempKey = `temp-videos/${baseName}.${ext}`;
  const thumbnailKey = `thumbnails/${baseName}.jpg`;

  // Get pre-signed URLs for video and thumbnail
  const [videoUploadUrl, thumbUploadUrl] = await Promise.all([
    getPreSignedUrl(tempKey, "upload"),
    getPreSignedUrl(thumbnailKey, "upload"),
  ]);
  onProgress?.(0.15);

  // Phase 2: Upload video and thumbnail (15-100% of progress)
  // Thumbnail is small, so weight video upload at 95% of upload progress
  const progressMap = { video: 0, thumb: 0 };
  const reportProgress = () => {
    const uploadProgress = progressMap.video * 0.95 + progressMap.thumb * 0.05;
    onProgress?.(0.15 + uploadProgress * 0.85);
  };

  await Promise.all([
    uploadFileWithProgress(
      videoUploadUrl,
      file,
      file.type || "video/mp4",
      (p) => { progressMap.video = p; reportProgress(); },
      600000 // 10 minute timeout for videos
    ),
    uploadFileWithProgress(
      thumbUploadUrl,
      thumbnailBlob,
      "image/jpeg",
      (p) => { progressMap.thumb = p; reportProgress(); }
    ),
  ]);

  return {
    tempKey,
    thumbnailUrl: `${STORAGE_PUBLIC_URL}/${thumbnailKey}`,
    width: metadata.width,
    height: metadata.height,
    aspectRatio: metadata.width / metadata.height,
    duration: metadata.duration,
    filenames: [tempKey, thumbnailKey],
  };
}

export interface FileUploadResult {
  fileUrl: string;
  filename: string;
}

/**
 * Upload an arbitrary file (document, PDF, etc.) to R2 storage.
 */
export async function uploadFile(
  file: File,
  onProgress?: (progress: number) => void
): Promise<FileUploadResult> {
  onProgress?.(0);

  const ext = file.name.split(".").pop() || "bin";
  const filename = `files/${Date.now()}.${ext}`;

  const uploadUrl = await getPreSignedUrl(filename, "upload");
  onProgress?.(0.1);

  await uploadFileWithProgress(
    uploadUrl,
    file,
    file.type || "application/octet-stream",
    (p) => { onProgress?.(0.1 + p * 0.9); }
  );

  return {
    fileUrl: `${STORAGE_PUBLIC_URL}/${filename}`,
    filename,
  };
}

/**
 * Delete files from R2 storage (for cleanup of orphaned uploads).
 */
export async function deleteFiles(filenames: string[]): Promise<void> {
  await Promise.all(
    filenames.map(async (filename) => {
      try {
        const deleteUrl = await getPreSignedUrl(filename, "delete");
        await fetch(deleteUrl, { method: "DELETE" });
      } catch (error) {
        console.error(`Failed to delete file ${filename}:`, error);
        // Don't throw - best effort cleanup
      }
    })
  );
}
